// Client-side PostHog init (Next 16 `instrumentation-client` entrypoint).
// Powers session replay + error tracking + web analytics. `autocapture`
// stays OFF — pageviews drive web analytics; per-element click capture
// isn't needed and would inflate event volume.
//
// WHY the non-obvious config:
//   - `capture_pageview: "history_change"` — captures on load AND App
//     Router SPA navigations via the History API, so no manual
//     usePathname pageview effect is needed.
//   - `maskAllInputs` — never capture what a user types. Page TEXT is not
//     masked (product content, internal-only replays); a name or email
//     the app renders gets `.ph-no-capture` per-element, and
//     lib/posthog/replay-masking.test.ts registers every such file. Any
//     email-shaped text is also masked everywhere (maskTextFn below).
//   - `person_profiles: "identified_only"` — no profile for anonymous
//     visitors (cost); identify() links the session post-login.
//   - no-ops when NEXT_PUBLIC_POSTHOG_KEY is unset (mirrors getPostHogClient).
//   - Privacy opt-out: if the browser sends Global Privacy Control or Do
//     Not Track (@medceptor/legal/privacy-signals), we skip init entirely
//     (no SDK, no cookie, no capture, no replay), as the privacy policy
//     promises. `respect_dnt` stays on as a belt, but alone it still loads
//     the SDK and sets the cookie before opting out. Server-side flag eval
//     (posthog-node) is unaffected; the browser SDK isn't used for gating,
//     so skipping it can't break a feature flag.
//   - Built envs only: `next dev` (NODE_ENV !== "production") never inits,
//     so local-dev sessions + HMR errors don't broadcast to the prod
//     project — no replay/pageview pollution, no error-tracking issues, no
//     Discord pings. Interim until per-environment tokens (roadmap). A dev-only
//     `NEXT_PUBLIC_POSTHOG_FORCE` escape hatch (below) can opt back into init
//     for local widget testing.
import posthog from "posthog-js";

import { deviceIdFromHandoff } from "@medceptor/experiments/posthog-device";
import {
  isDoNotTrackSignal,
  isGlobalPrivacyControlSignal,
} from "@medceptor/legal/privacy-signals";

import { env } from "@/lib/env";
import { POSTHOG_DEV_FORCED } from "@/lib/posthog/dev-force";
import { maskEmailAddresses } from "@/lib/posthog/mask-emails";
import { redactEventUrlSecrets } from "@/lib/posthog/redact-url-secrets";

const privacyOptOut =
  isGlobalPrivacyControlSignal(globalThis) || isDoNotTrackSignal(globalThis);

// Dev-only escape hatch: load the SDK under `next dev` so client-capture
// widgets (NPS, scenario + rubric feedback) render locally. Enable on demand:
// `env NEXT_PUBLIC_POSTHOG_FORCE=1 pnpm dev` (the NEXT_PUBLIC_ prefix is
// required — client code only sees inlined NEXT_PUBLIC_* vars). Guarded on
// NODE_ENV so it can NEVER turn on in a production/preview build. (It loads
// opted out of capturing — see the config block below.)
const forceLocal = POSTHOG_DEV_FORCED;

if (
  env.NEXT_PUBLIC_POSTHOG_KEY &&
  // The dev hatch ignores the privacy signals: it loads opted out of
  // capturing (below), so it sends nothing, and a developer whose browser
  // sends DNT still needs the widgets to render.
  ((process.env.NODE_ENV === "production" && !privacyOptOut) || forceLocal)
) {
  posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
    respect_dnt: true,
    // Reverse proxy: beacons go to our own origin `/medceptor-relay`
    // (rewritten to PostHog in next.config.js; path-choice rationale
    // there). ui_host keeps the toolbar/links pointing at PostHog.
    //
    // CROSS-SITE INVARIANT (CLAUDE.md gotcha #42): this app and apps/marketing
    // share one PostHog project, so `medceptor.com` -> `app.medceptor.com` is one
    // person via a single `.medceptor.com` cookie. Editing `api_host`,
    // `persistence` or `cross_subdomain_cookie` here, or adding `cookie_name` /
    // `persistence_name`, severs the visits->signup funnel with no runtime
    // signal. Both sides are pinned: apps/marketing in
    // `lib/posthog-config.test.ts`, this app in `instrumentation-client.test.ts`
    // (which also cross-checks `api_host` against next.config.js's rewrites and
    // proxy.ts's auth-wall exclusion). A red test here is load-bearing.
    api_host: "/medceptor-relay",
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: "history_change",
    capture_pageleave: "if_capture_pageview",
    // Error tracking: autocapture uncaught errors + unhandled promise
    // rejections (window-level). React render errors that hit a Next
    // error boundary are reported server-side via `onRequestError` in
    // instrumentation.ts; add a route `error.tsx` calling
    // `posthog.captureException` if a specific boundary needs coverage.
    capture_exceptions: true,
    session_recording: {
      maskAllInputs: true,
      // Backstop for the per-element `.ph-no-capture` masking: every recorded
      // text node passes through maskEmailAddresses, which hides only
      // email-shaped text, so a toast, a server error or a future page that
      // shows an email can't leak one into a replay.
      maskTextSelector: "*",
      maskTextFn: maskEmailAddresses,
    },
    // Belt, no longer the mechanism. The invite token stopped riding in URLs
    // when `/invite/<token>` became a cookie handoff (lib/classes/invite-cookie.ts),
    // so nothing should reach this hook any more. It stays for links already
    // sitting in inboxes, and for anything a future feature puts in a path.
    // Never drops an event (see the helper), so it cannot cost a pageview or
    // touch the cross-subdomain funnel invariant above.
    before_send: redactEventUrlSecrets,
    // The proxy's drawn device id, so the arm's exposure and signup are one person (ENG-348).
    get_device_id: deviceIdFromHandoff,
    // Dev escape hatch: render widgets WITHOUT sending to the prod project.
    // Capture starts opted out (so clicks no-op the network); `debug` logs each
    // attempted capture to the console. To send real events for an end-to-end
    // check, run `posthog.opt_in_capturing()` in the browser console.
    ...(forceLocal ? { opt_out_capturing_by_default: true, debug: true } : {}),
  });
}
